export type SyncCursor = {
  modifiedAt: string;
  tieBreaker: string[];
};

type SyncCursorInput = {
  modifiedAt: string | undefined | null;
  tieBreaker: string[];
};

export const parseSyncCursor = (value: string | undefined): SyncCursor | undefined => {
  if (!value) return undefined;

  try {
    const parsed = JSON.parse(value);
    if (
      parsed &&
      typeof parsed.modifiedAt === 'string' &&
      Array.isArray(parsed.tieBreaker) &&
      parsed.tieBreaker.every((part: unknown) => typeof part === 'string')
    ) {
      return {
        modifiedAt: parsed.modifiedAt,
        tieBreaker: parsed.tieBreaker,
      };
    }
  } catch {
    // Existing sync_state rows stored only the modified_at ISO string.
  }

  return { modifiedAt: value, tieBreaker: [] };
};

export const serializeSyncCursor = (cursor: SyncCursor): string => JSON.stringify(cursor);

export const getLatestSyncCursor = (inputs: SyncCursorInput[]): SyncCursor | undefined => {
  const sortedInputs = inputs
    .filter((input): input is SyncCursorInput & { modifiedAt: string } => (
      typeof input.modifiedAt === 'string' && input.modifiedAt.length > 0
    ))
    .sort((a, b) => compareSyncCursorParts(a.modifiedAt, a.tieBreaker, b.modifiedAt, b.tieBreaker));

  const latest = sortedInputs[sortedInputs.length - 1];
  if (!latest) return undefined;

  return {
    modifiedAt: latest.modifiedAt,
    tieBreaker: latest.tieBreaker,
  };
};

const compareSyncCursorParts = (
  aModifiedAt: string,
  aTieBreaker: string[],
  bModifiedAt: string,
  bTieBreaker: string[]
): number => {
  const modifiedAtCompare = aModifiedAt.localeCompare(bModifiedAt);
  if (modifiedAtCompare !== 0) return modifiedAtCompare;

  const maxLength = Math.max(aTieBreaker.length, bTieBreaker.length);
  for (let i = 0; i < maxLength; i += 1) {
    const aPart = aTieBreaker[i] ?? '';
    const bPart = bTieBreaker[i] ?? '';
    const partCompare = aPart.localeCompare(bPart);
    if (partCompare !== 0) return partCompare;
  }

  return 0;
};
